import React, { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { LoaderComponent } from "@auth/PrivateRoute";
import { getStatusMeta } from "@screens/new_RM";
import NewSearch from "./NewSearch";
import BackButton from "@/components/ui/backButton";
import { convertMonthsToYears } from "@component/formfields/EditFormTailwind";

const baseFontFamily =
  'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", Segoe UI Symbol, "Noto Color Emoji"';

const FilterDropdown = ({
  data = [],
  filterKeys = [],
  filterLabels = {},
  selectedFilters = {},
  setSelectedFilters = () => {},
  onApply,
  onClear,
  className = "",
  isLoading,
  onSearch,
}) => {
  const [expandedKeys, setExpandedKeys] = useState({});
  const [loading, setLoading] = useState({ clear: false, apply: false });
  const [globalSearch, setGlobalSearch] = useState("");

  // Compute filter options
  const filterOptions = useMemo(() => {
    const result = {};
    filterKeys.forEach((key) => {
      const transformedValues = data
        .map((item) => {
          let v = item[key];
          if (v === null || v === undefined || v === " ") return "N/A";

          if (key === "band_" && String(v) === "-1") return "Contractor";
          if (key === "status_") {
            if (v === "TRUE ") return "Active";
            if (v === "FALSE") return "In-Active";
          }

          if (["total_yoe", "wavicle_yoe", "yoe_before_wavicle"].includes(key)) {
            return convertMonthsToYears(v);
          }

          return v;
        });

      // Deduplicate after transformation
      result[key] = [...new Set(transformedValues)];
    });
    return result;
  }, [data, filterKeys]);

  const handleToggle = (key, value) => {
    let actualValue = value;

    if (key === "band_" && value === "Contractor") actualValue = -1;
    if (key === "status_") {
      if (value === "Active") actualValue = "TRUE ";
      else if (value === "In-Active") actualValue = "FALSE";
    }
    if (value === "N/A") actualValue = " ";

    setSelectedFilters((prev) => {
      const prevValues = prev[key] || [];
      const updatedValues = prevValues.includes(actualValue)
        ? prevValues.filter((v) => v !== actualValue)
        : [...prevValues, actualValue];
      return { ...prev, [key]: updatedValues };
    });
  };

  const handleClear = async () => {
    setLoading((prev) => ({ ...prev, clear: true }));
    try {
      setSelectedFilters({});
      await onClear?.();
    } finally {
      setLoading((prev) => ({ ...prev, clear: false }));
    }
  };

  const handleApply = async () => {
    setLoading((prev) => ({ ...prev, apply: true }));
    try {
      await onApply?.(selectedFilters);
    } finally {
      setLoading((prev) => ({ ...prev, apply: false }));
    }
  };

  const toggleExpand = (key) => {
    setExpandedKeys((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Check if any filter has data after search
  const hasData = filterKeys.some(
    (key) =>
      (filterOptions[key] || []).filter((option) =>
        String(option).toLowerCase().includes(globalSearch.toLowerCase())
      ).length > 0
  );

  return (
    <div
      className={`font-sans w-80 bg-white dark:bg-gray-900 shadow-lg rounded-2xl p-4 relative ${className}`}
      style={{ fontFamily: baseFontFamily }}
    >
      {isLoading ? (
        <LoaderComponent height="80%" />
      ) : (
        <>
          {/* Global Search */}
          <div className="mb-3">
            <NewSearch
              value={globalSearch}
              onChange={(e) => setGlobalSearch(e.target.value)}
              onSearch={onSearch}
              isVisibleBtn={false}
              width="100%"
              placeHolder="Search filters..."
            />
          </div>

          {/* Filter Sections */}
          <div className="max-h-80 overflow-y-auto pr-1">
            {hasData ? (
              filterKeys.map((key, idx) => {
                const originalOptions = filterOptions[key] || [];
                const filteredOptions = originalOptions.filter((option) =>
                  String(option).toLowerCase().includes(globalSearch.toLowerCase())
                );

                // CASE 1: No data exists at all for this label → show N/A
                if (originalOptions.length === 0) {
                  return (
                    <div key={key} className="mb-3">
                      <p className="text-sm font-semibold text-gray-600 dark:text-gray-300 mb-1">
                        {filterLabels[key] || key}
                      </p>
                      <p className="text-xs text-gray-400 italic pl-1">N/A</p>
                    </div>
                  );
                }

                // CASE 2: Data exists but search removed all → skip this label
                if (filteredOptions.length === 0) return null;

                // CASE 3: Normal render
                const isExpanded = expandedKeys[key];
                const visibleOptions = isExpanded
                  ? filteredOptions
                  : filteredOptions.slice(0, 10);

                return (
                  <div key={key} className="mb-3">
                    <p className="text-sm font-semibold text-gray-600 dark:text-gray-300 mb-1">
                      {filterLabels[key] || key}
                    </p>

                    <div className="flex flex-col gap-1 pl-2 mt-1">
                      {visibleOptions.map((option) => {
                        if (key === "allocation_status") {
                          const { status, color, bg } = getStatusMeta(option);
                          return (
                            <label
                              key={option}
                              className={`flex items-center gap-2 text-sm cursor-pointer ${color} ${bg}`}
                            >
                              <input
                                type="checkbox"
                                checked={selectedFilters[key]?.includes(option) || false}
                                onChange={() => handleToggle(key, option)}
                                className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 accent-blue-500"
                              />
                              {status}
                            </label>
                          );
                        }

                        return (
                          <label
                            key={option}
                            className="flex items-center gap-2 text-sm text-foreground dark:text-gray-200 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={
                                selectedFilters[key]?.includes(
                                  key === "band_" && option === "Contractor"
                                    ? -1
                                    : key === "status_" && option === "Active"
                                    ? "TRUE "
                                    : key === "status_" && option === "In-Active"
                                    ? "FALSE"
                                    : option === "N/A"
                                    ? " "
                                    : option
                                ) || false
                              }
                              onChange={() => handleToggle(key, option)}
                              className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 accent-blue-500"
                            />
                            <span
                              className="truncate max-w-[220px] group-hover:whitespace-normal group-hover:break-words"
                              title={String(option)}
                            >
                              {key === "band_" && option === -1
                                ? "Contractor"
                                : key === "status_" && option === "TRUE "
                                ? "Active"
                                : key === "status_" && option === "FALSE"
                                ? "In-Active"
                                : option}
                            </span>
                          </label>
                        );
                      })}

                      {filteredOptions.length > 10 && (
                        <button
                          onClick={() => toggleExpand(key)}
                          className="text-xs text-primary dark:text-blue-400 mt-1 self-start"
                        >
                          {isExpanded ? "View Less" : "View More"}
                        </button>
                      )}
                    </div>

                    {idx < filterKeys.length - 1 && (
                      <hr className="border-t border-gray-200 dark:border-gray-700 mt-3" />
                    )}
                  </div>
                );
              })
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 text-center">
                No data found
              </p>
            )}
          </div>



      <div className="flex gap-2 mt-3">
        <BackButton
          label="Clear"
          startIcon={true}
          onClick={handleClear}
          className="flex-1 flex items-center justify-center gap-2"
          disabled={loading.clear}
        >
          {loading.clear && (
            <span className="animate-spin rounded-full border-2 border-gray-400 border-t-transparent w-4 h-4"></span>
          )}
        </BackButton>

        <Button
          variant="default"
          onClick={handleApply}
          className="flex-1 flex items-center justify-center gap-2"
          disabled={loading.apply}
        >
          {loading.apply && (
            <span className="animate-spin rounded-full border-2 border-white border-t-transparent w-4 h-4"></span>
          )}
          Apply
        </Button>
      </div>
      </>
      )}
    </div>
  );
};

export default FilterDropdown;
